/**
 * BASE: Gemini hears the words, the local MMS aligner measures them.
 *
 * The default, and the one a phone can never run (it needs the aligner helper),
 * which is why `firstPass` falls through to the legacy pipeline when the helper
 * does not answer.
 *
 * THE NOISE DETOUR IS THE SUBTLE PART. The aligner needs pronounceable text:
 * words and fillers align, a cough does not. Noise tokens are therefore held
 * back from the alignment call — but NOT discarded. Discarding them used to
 * lose them entirely, so their audio was silently absorbed by a neighbouring
 * word; instead their position in the spoken sequence is remembered and
 * `tileFromWords` places each one in the gap between the neighbours it was
 * heard between.
 */

import { alignTranscript } from '../aligner';
import { transcribeVerbatim, type VerbatimToken } from '../gemini';
import { tileFromWords, type AlignedTilesResult, type BuildTilesArgs } from './alignedTiling';

export async function baseGemini(args: BuildTilesArgs): Promise<AlignedTilesResult> {
  const { uploadWav, signal, onPhase } = args;

  /* -- 1. words, no timestamps ------------------------------------- */
  onPhase?.('Transcribing (words only)…', 0.05);
  const verbatim = await transcribeVerbatim({ audio: uploadWav, mimeType: 'audio/wav', signal });

  // The aligner needs pronounceable text: words and fillers align, a cough
  // does not. Noise tokens are therefore held back from the alignment call —
  // but NOT discarded (that used to lose them entirely, so their audio was
  // absorbed by a neighbouring word). Their position in the spoken sequence
  // is remembered, and after alignment each one is placed in the gap between
  // the neighbours it was heard between.
  const alignable: VerbatimToken[] = [];
  /** noise token -> how many alignable tokens preceded it */
  const pendingNoise: Array<{ token: VerbatimToken; afterIndex: number }> = [];
  for (const t of verbatim.tokens) {
    if (t.kind === 'noise') pendingNoise.push({ token: t, afterIndex: alignable.length - 1 });
    else alignable.push(t);
  }
  if (alignable.length === 0) throw new Error('Nothing alignable was transcribed.');

  /* -- 2. measured timestamps --------------------------------------- */
  onPhase?.(`Aligning ${alignable.length} words…`, 0.45);
  // Aligner splits the transcript on whitespace: one token per word required.
  const transcript = alignable.map((t) => t.text.replace(/\s+/g, '_')).join(' ');
  const words = await alignTranscript({
    wav: uploadWav,
    transcript,
    language: verbatim.language,
    signal,
  });
  if (words.length !== alignable.length) {
    throw new Error(
      `Aligner returned ${words.length} words for ${alignable.length} tokens — cannot map them safely.`,
    );
  }

  /* -- 3. tile the timeline (shared by both engines) ------------------ */
  onPhase?.('Building the labeled timeline…', 0.85);
  return tileFromWords(args, alignable, words, pendingNoise, verbatim.language);
}
